import { useState, useEffect } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { FeedbackTable, type FeedbackEntry } from "../components/admin/FeedbackTable";
import { TopBar } from "../components/TopBar";
import styles from "../styles/pages/AdminSubPage.module.css";

export function AdminFeedbackPage() {
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  const [entries, setEntries] = useState<FeedbackEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isLoaded && userId) load();
  }, [isLoaded, userId]);

  async function load() {
    setLoading(true);
    try {
      const data = await fetch("/api/feedback").then((r) => r.json());
      setEntries(data.entries);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    setDeleting((prev) => new Set(prev).add(id));
    try {
      await fetch(`/api/feedback/${id}`, { method: "DELETE" });
      setEntries((prev) => prev?.filter((e) => e.id !== id) ?? null);
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  if (!isLoaded || !userId || !isAdmin) return null;

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
        <div className={styles.header}>
          <Link to="/admin" className={styles.back}>← Admin</Link>
          <h1 className={styles.title}>User Feedback</h1>
        </div>
        <FeedbackTable
          entries={entries}
          loading={loading}
          deleting={deleting}
          onLoad={load}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}
